import React, { useEffect } from 'react';

import { l10n } from '@ott/l10n';
import { LinkButton } from '@ott/link';
import { useMessage } from '@ott/message';
import KM from '@ott/utility-km';

import { getErrorByCode } from '../../helpers/error';
import ScanIcon from './ScanIcon';

import styles from './PassportPhotoRecognition.scss';

type Props = {
  error: string;
  isRecognitionPartial: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  handleUploadPassportPhoto: (file: File) => void;
  abortUploadPassportPhoto: () => void;
};

const statusesList = [
  l10n('virtualCard.passportRecognition.statusesList.upload'),
  l10n('virtualCard.passportRecognition.statusesList.checkSize'),
  l10n('virtualCard.passportRecognition.statusesList.processing'),
  l10n('virtualCard.passportRecognition.statusesList.recognition'),
  l10n('virtualCard.passportRecognition.statusesList.loading'),
  l10n('virtualCard.passportRecognition.statusesList.fillForm'),
];

export const PassportPhotoRecognition: React.FC<Props> = (props) => {
  const {
    error,
    isRecognitionPartial,
    isLoading,
    isSuccess,
    handleUploadPassportPhoto,
    abortUploadPassportPhoto,
  } = props;
  const [isUploadButtonHide, setIsUploadButtonHide] = React.useState(false);
  const [isAbortButtonShow, setIsAbortButtonShow] = React.useState(false);
  const [isAbortUpload, setIsAbortUpload] = React.useState(false);
  const [statusCounter, setStatusCounter] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const messageApi = useMessage();

  const onUploadPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    KM('Virtual_Card_Identification_Photo_Click');

    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files[0].size > 5 * 1024 * 1024) {
        messageApi.error(l10n('virtualCard.passportRecognition.error.maxSizeLimit'), {
          placement: 'top',
        });

        return;
      }

      setIsUploadButtonHide(true);
      handleUploadPassportPhoto(e.target.files[0]);
    }
  };

  const resetLoadingState = () => {
    setProgress(0);
    setStatusCounter(0);
    setIsAbortUpload(false);
    setIsUploadButtonHide(false);
    setIsAbortButtonShow(false);
  };

  const onAbortUpload = () => {
    setIsAbortUpload(true);

    abortUploadPassportPhoto();
  };

  useEffect(() => {
    let loadingTimer: NodeJS.Timeout;
    if (isLoading) {
      loadingTimer = setInterval(() => {
        setProgress((prevProgress) => (prevProgress >= 80 ? prevProgress : prevProgress + 1));
      }, 100);
    }

    return () => {
      clearInterval(loadingTimer);
    };
  }, [isLoading]);

  useEffect(() => {
    let statusTimer: NodeJS.Timeout;
    if (isLoading) {
      statusTimer = setInterval(() => {
        setStatusCounter((prevStatusCounter) =>
          prevStatusCounter === statusesList.length - 1 ? prevStatusCounter : prevStatusCounter + 1
        );
      }, 1500);
    }

    return () => {
      clearInterval(statusTimer);
    };
  }, [isLoading]);

  useEffect(() => {
    let showAbortButtonTimer: NodeJS.Timeout;
    if (isLoading) {
      showAbortButtonTimer = setTimeout(() => {
        setIsAbortButtonShow(true);
      }, 4000);
    }

    return () => {
      clearInterval(showAbortButtonTimer);
    };
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess && !isRecognitionPartial) {
      setProgress(100);
      messageApi.success(l10n('virtualCard.passportRecognition.success.full'), {
        placement: 'top',
      });
      resetLoadingState();
    }
  }, [isRecognitionPartial, isSuccess, messageApi]);

  useEffect(() => {
    if (isRecognitionPartial) {
      messageApi.success(l10n('virtualCard.passportRecognition.success.partial'), {
        placement: 'top',
      });
      resetLoadingState();
    }
  }, [isRecognitionPartial, messageApi]);

  useEffect(() => {
    if (error && !isAbortUpload) {
      messageApi.error(getErrorByCode(error), { placement: 'top' });
      resetLoadingState();
    }
  }, [error, messageApi, isAbortUpload]);

  useEffect(() => {
    if (isAbortUpload) {
      messageApi.error(l10n('virtualCard.passportRecognition.error.abort'), { placement: 'top' });
      resetLoadingState();
    }
  }, [isAbortUpload, messageApi]);

  const renderUploadButton = () => (
    <div className={styles.uploadButtonContainer}>
      <input
        id="uploadPassportPhoto"
        type="file"
        name="uploadPassportPhoto"
        accept="image/png, image/jpeg, application/pdf"
        className={styles.uploadInput}
        data-locator="passport-recognition-upload-button-input"
        onChange={onUploadPhoto}
      />
      <label
        htmlFor="uploadPassportPhoto"
        className={styles.uploadLabel}
        data-locator="passport-recognition-upload-button"
      >
        <ScanIcon className={styles.uploadIcon} />
        <span className={styles.uploadText}>{l10n('virtualCard.passportRecognition.title')}</span>
      </label>
    </div>
  );

  const renderLoadContainer = () => (
    <div className={styles.loadContainer} data-locator="passport-recognition-loading-container">
      <div className={styles.statusContainer}>
        {isLoading && (
          <span className={styles.loading} data-locator="passport-recognition-loading-status">
            {statusesList[statusCounter]}...
          </span>
        )}
      </div>
      {isAbortButtonShow && (
        <LinkButton
          className={styles.statusButton}
          specificLocators="passport-recognition-loading-abort"
          onClick={onAbortUpload}
        >
          {l10n('virtualCard.passportRecognition.abortButton')}
        </LinkButton>
      )}
      <div className={styles.progressBar} data-locator="passport-recognition-loading-progressBar">
        <div className={styles.line} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );

  return (
    <div className={styles.container} data-locator="passport-recognition-container">
      {isUploadButtonHide ? renderLoadContainer() : renderUploadButton()}
      <span className={styles.caption} data-locator="passport-recognition-caption">
        {l10n('virtualCard.passportRecognition.caption')}
      </span>
    </div>
  );
};
